import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./components/ui/dialog";
import { AlertTriangle, Plus, Minus, X, Users, Baby, Loader2, Globe, Clock } from "lucide-react";
import { Label } from "./components/ui/label";
import { GuidedLanguageRadios } from "./GuidedLanguageRadios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";

// Members only see "places left" once availability is this low.
const LOW_STOCK_THRESHOLD = 3;

/**
 * CustomRowsBookingDialog — member-facing booking flow for events using the
 * new `custom_rows` pricing model.
 *
 * The member picks quantities across the event's admin-defined price rows
 * for themselves and (optionally) for a linked partner, then adds 0..3
 * minors each pinned to a chosen row. Age is not used to price anything —
 * the member self-selects the correct ticket for each attendee, guarded by
 * a red-bordered disclaimer above the total.
 */
const CustomRowsBookingDialog = ({ isOpen, onClose, event, currentUser, onSuccess }) => {
  const rows = (event?.custom_price_rows || []).filter((r) => !r.admin_only);
  const [memberQty, setMemberQty] = useState({}); // { row_id: qty }
  const [includePartner, setIncludePartner] = useState(false);
  const [partnerQty, setPartnerQty] = useState({});
  const [partner, setPartner] = useState(null); // {id, first_name, last_name, email}
  const [minors, setMinors] = useState([]); // [{first_name, last_name, age, row_id}]
  const [disclaimer, setDisclaimer] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [guidedLanguage, setGuidedLanguage] = useState("");
  const [comments, setComments] = useState("");
  const guidedRequired = Boolean(event?.guided_visit);

  // Sold-out → waitlist branch state.
  const [onWaitlist, setOnWaitlist] = useState(false);
  const [waitlistTickets, setWaitlistTickets] = useState(1);
  const [waitlistPartner, setWaitlistPartner] = useState(false);
  const [waitlistMinors, setWaitlistMinors] = useState(0);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const isSoldOut = remaining != null && remaining <= 0;
  const [categoryLeft, setCategoryLeft] = useState({}); // { row_id: remaining|null }

  // Reset local state each time the dialog opens on a new event.
  useEffect(() => {
    if (!isOpen) return;
    const firstAdult = rows.find((r) => !r.minors_only);
    const seed = rows.reduce((acc, r) => ({ ...acc, [r.id]: firstAdult && r.id === firstAdult.id ? 1 : 0 }), {});
    setMemberQty(seed);
    setPartnerQty(rows.reduce((acc, r) => ({ ...acc, [r.id]: 0 }), {}));
    setIncludePartner(false);
    setPartner(null);
    setMinors([]);
    setAccepted(false);
    setGuidedLanguage("");
    setComments("");
    setWaitlistTickets(1);
    setWaitlistPartner(false);
    setWaitlistMinors(0);
    setOnWaitlist(false);
  }, [isOpen, event?.id]);

  // Pull the disclaimer text from global settings (editable by admins).
  useEffect(() => {
    if (!isOpen) return;
    axios.get("/settings/booking-disclaimer")
      .then((res) => setDisclaimer(res.data?.text || ""))
      .catch(() => setDisclaimer("You are responsible for choosing the correct ticket type for each attendee."));
  }, [isOpen]);

  // Availability preview (best-effort).
  useEffect(() => {
    if (!isOpen || !event?.id) return;
    axios.get(`events/${event.id}/availability`)
      .then((res) => setRemaining(res.data?.remaining_tickets ?? null))
      .catch(() => setRemaining(null));
  }, [isOpen, event?.id]);

  // Per-category availability (only categories with a ticket limit get a number).
  useEffect(() => {
    if (!isOpen || !event?.id) return;
    axios.get(`events/${event.id}/category-availability`)
      .then((res) => {
        const left = {};
        (res.data?.categories || []).forEach((c) => { left[c.row_id] = c.remaining; });
        setCategoryLeft(left);
        // Don't pre-select a sold-out category for the member.
        const firstOpen = rows.find((r) => !r.minors_only && left[r.id] !== 0);
        setMemberQty((prev) => {
          const picked = Object.keys(prev).find((k) => (prev[k] || 0) > 0);
          if (!picked || left[picked] !== 0) return prev;
          return rows.reduce((acc, r) => ({ ...acc, [r.id]: firstOpen && r.id === firstOpen.id ? 1 : 0 }), {});
        });
      })
      .catch(() => setCategoryLeft({}));
  }, [isOpen, event?.id]);

  // If the event turns out to be sold out, check whether the member is
  // already on the waiting list so we don't offer them a duplicate join.
  useEffect(() => {
    if (!isOpen || !event?.id) return;
    axios.get(`events/${event.id}/my-waitlist-status`)
      .then((res) => setOnWaitlist(Boolean(res.data?.on_waitlist)))
      .catch(() => setOnWaitlist(false));
  }, [isOpen, event?.id]);

  // Auto-load the member's linked partner (if any). Silent on failure.
  useEffect(() => {
    if (!isOpen) return;
    axios.get("/users/my-partner")
      .then((res) => setPartner(res.data?.partner || null))
      .catch(() => setPartner(null));
  }, [isOpen]);

  const leftOf = (rowId) => (categoryLeft[rowId] == null ? null : categoryLeft[rowId]);
  const rowSoldOut = (rowId) => leftOf(rowId) === 0;
  const firstOpenRowId = rows.find((r) => !rowSoldOut(r.id))?.id;
  // Rows flagged "minors only" are offered in the Minors section only.
  const adultRows = rows.filter((r) => !r.minors_only);
  const firstOpenAdultRowId = adultRows.find((r) => !rowSoldOut(r.id))?.id;

  const priceOf = (rowId) => Number(rows.find((r) => r.id === rowId)?.amount || 0);
  const labelOf = (rowId) => rows.find((r) => r.id === rowId)?.label || "—";

  const memberSubtotal = useMemo(
    () => rows.reduce((sum, r) => sum + priceOf(r.id) * (memberQty[r.id] || 0), 0),
    [memberQty, rows]
  );
  const partnerSubtotal = useMemo(
    () => (includePartner ? rows.reduce((sum, r) => sum + priceOf(r.id) * (partnerQty[r.id] || 0), 0) : 0),
    [partnerQty, includePartner, rows]
  );
  const minorsSubtotal = useMemo(
    () => minors.reduce((sum, m) => sum + priceOf(m.row_id), 0),
    [minors, rows]
  );
  const total = useMemo(
    () => Number((memberSubtotal + partnerSubtotal + minorsSubtotal).toFixed(2)),
    [memberSubtotal, partnerSubtotal, minorsSubtotal]
  );
  const totalTickets = useMemo(() => {
    const m = Object.values(memberQty).reduce((a, b) => a + (Number(b) || 0), 0);
    const p = includePartner ? Object.values(partnerQty).reduce((a, b) => a + (Number(b) || 0), 0) : 0;
    return m + p + minors.length;
  }, [memberQty, partnerQty, includePartner, minors]);

  // Single-choice pickers: exactly one row can be selected for the member and
  // one for the partner (business rule — no bulk tickets in one booking).
  // Selecting the currently-selected row clears the selection ("no ticket
  // for me / partner"), which is a valid state so a member can book only
  // for the partner or only for minors.
  const memberSelectedRow = Object.keys(memberQty).find((k) => (memberQty[k] || 0) > 0) || "";
  const partnerSelectedRow = Object.keys(partnerQty).find((k) => (partnerQty[k] || 0) > 0) || "";
  const setMemberRow = (rowId) => {
    setMemberQty(rows.reduce(
      (acc, r) => ({ ...acc, [r.id]: r.id === rowId ? 1 : 0 }),
      {},
    ));
  };
  const clearMemberRow = () => {
    setMemberQty(rows.reduce((acc, r) => ({ ...acc, [r.id]: 0 }), {}));
  };
  const setPartnerRow = (rowId) => {
    setPartnerQty(rows.reduce(
      (acc, r) => ({ ...acc, [r.id]: r.id === rowId ? 1 : 0 }),
      {},
    ));
  };
  const clearPartnerRow = () => {
    setPartnerQty(rows.reduce((acc, r) => ({ ...acc, [r.id]: 0 }), {}));
  };

  const addMinor = () => {
    if (minors.length >= 3) return;
    setMinors((list) => [...list, { first_name: "", last_name: "", age: "", row_id: rows.find((r) => r.minors_only && !rowSoldOut(r.id))?.id || firstOpenRowId }]);
  };
  const updateMinor = (idx, patch) => {
    setMinors((list) => list.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };
  const removeMinor = (idx) => {
    setMinors((list) => list.filter((_, i) => i !== idx));
  };

  const waitlistTotalTickets = waitlistTickets + (waitlistPartner ? 1 : 0) + waitlistMinors;

  const joinWaitlist = async () => {
    if (waitlistPartner && !partner?.id) {
      toast.error("Link a partner first (from the user menu) before requesting a ticket for them.");
      return;
    }
    if (waitlistTotalTickets < 1) {
      toast.error("Please request at least one ticket.");
      return;
    }
    setJoiningWaitlist(true);
    try {
      const res = await axios.post(`events/${event.id}/waitlist`, {
        requested_tickets: waitlistTotalTickets,
        partner_included: waitlistPartner,
        minor_count: waitlistMinors,
      });
      toast.success(`Added to waiting list (position #${res.data?.position ?? "?"})`);
      setOnWaitlist(true);
      onSuccess?.({ waitlisted: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join waiting list");
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const submit = async () => {
    if (!accepted) {
      toast.error("Please accept the responsibility disclaimer.");
      return;
    }
    if (totalTickets === 0) {
      toast.error("Please select at least one ticket.");
      return;
    }
    for (const m of minors) {
      if (!m.first_name.trim() || !m.last_name.trim() || !m.age || !m.row_id) {
        toast.error("Every minor needs a first name, last name, age and ticket type.");
        return;
      }
      const age = parseInt(m.age, 10);
      if (!Number.isFinite(age) || age < 1 || age > 17) {
        toast.error("Minor age must be between 1 and 17.");
        return;
      }
    }
    if (includePartner && !partner?.id) {
      toast.error("You need to link a partner first (from the user menu) before booking tickets for them.");
      return;
    }
    const perRow = {};
    Object.entries(memberQty).forEach(([k, v]) => { perRow[k] = (perRow[k] || 0) + (Number(v) || 0); });
    if (includePartner) Object.entries(partnerQty).forEach(([k, v]) => { perRow[k] = (perRow[k] || 0) + (Number(v) || 0); });
    minors.forEach((m) => { perRow[m.row_id] = (perRow[m.row_id] || 0) + 1; });
    for (const [rowId, want] of Object.entries(perRow)) {
      const left = leftOf(rowId);
      if (want > 0 && left != null && want > left) {
        toast.error(left === 0
          ? `${labelOf(rowId)} is sold out. Please choose another category.`
          : `Only ${left} ticket${left !== 1 ? "s" : ""} left for ${labelOf(rowId)} — you selected ${want}.`);
        return;
      }
    }
    if (guidedRequired && !guidedLanguage) {
      toast.error("Please pick a language for the guided visit (Italian, English, or Both).");
      return;
    }

    setSubmitting(true);
    try {
      const memberSelections = rows
        .filter((r) => (memberQty[r.id] || 0) > 0)
        .map((r) => ({ row_id: r.id, quantity: memberQty[r.id] }));
      const partnerSelections = includePartner
        ? rows.filter((r) => (partnerQty[r.id] || 0) > 0).map((r) => ({ row_id: r.id, quantity: partnerQty[r.id] }))
        : [];

      const res = await axios.post("/bookings/custom-rows", {
        event_id: event.id,
        member_selections: memberSelections,
        partner_selections: partnerSelections,
        partner_user_id: includePartner ? partner?.id : null,
        minors: minors.map((m) => ({
          first_name: m.first_name.trim(),
          last_name: m.last_name.trim(),
          age: parseInt(m.age, 10),
          row_id: m.row_id,
        })),
        origin_url: window.location.origin,
        disclaimer_accepted: true,
        guided_visit_language: guidedRequired ? guidedLanguage : null,
        notes: comments.trim(),
      });

      if (res.data?.checkout_url) {
        // Persist a hint so the return page can wire up confirmation.
        try {
          sessionStorage.setItem("last_booking_ids", JSON.stringify(res.data.booking_ids || []));
        } catch { /* private mode */ }
        window.location.href = res.data.checkout_url;
        return;
      }
      toast.success("Booking created.");
      onSuccess?.(res.data);
      onClose?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Duplicate booking → offer the existing Stripe checkout so the user can resume.
      if (err.response?.status === 409 && err.response?.data?.checkout_url) {
        window.location.href = err.response.data.checkout_url;
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="custom-rows-booking-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl">
            {isSoldOut ? `Waiting list — ${event?.title || "Event"}` : (event?.title || "Book event")}
          </DialogTitle>
          <DialogDescription>
            {event?.event_date && new Date(event.event_date).toLocaleString()}
            {isSoldOut ? (
              <span className="ml-2 text-orange-700 font-medium">— Sold out</span>
            ) : (
              <>
                {" "}· {rows.length} ticket type{rows.length !== 1 ? "s" : ""}
                {remaining != null && remaining <= LOW_STOCK_THRESHOLD && (
                  <span className="ml-2 text-orange-700 font-medium" data-testid="booking-places-left">— Only {remaining} place{remaining !== 1 ? "s" : ""} left</span>
                )}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isSoldOut ? (
          <div className="space-y-4" data-testid="custom-rows-waitlist-branch">
            {onWaitlist ? (
              <div
                className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3"
                data-testid="on-waitlist-msg"
              >
                <Clock className="h-4 w-4 text-blue-700 mt-0.5" />
                <p className="text-sm text-blue-900">
                  You are on the waiting list for this event. We will contact you if a spot opens up.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 p-3">
                  <AlertTriangle className="h-4 w-4 text-orange-700 mt-0.5" />
                  <p className="text-xs text-orange-900 leading-relaxed">
                    This event is sold out. You can join the waiting list — no payment required. We'll contact you if a spot opens up.
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="wl-tickets" className="text-xs text-slate-600 mb-1 block">
                      My ticket
                    </Label>
                    <Select
                      value={String(waitlistTickets)}
                      onValueChange={(v) => setWaitlistTickets(parseInt(v, 10))}
                    >
                      <SelectTrigger id="wl-tickets" className="h-9" data-testid="waitlist-tickets">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="wl-partner" className="text-xs text-slate-600 mb-1 block">
                      Partner
                    </Label>
                    <Select
                      value={waitlistPartner ? "1" : "0"}
                      onValueChange={(v) => setWaitlistPartner(v === "1")}
                      disabled={!partner}
                    >
                      <SelectTrigger id="wl-partner" className="h-9" data-testid="waitlist-partner">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">No</SelectItem>
                        <SelectItem value="1" disabled={!partner}>Yes</SelectItem>
                      </SelectContent>
                    </Select>
                    {!partner && (
                      <p className="text-[10px] text-slate-500 mt-1">Link a partner first from the user menu.</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="wl-minors" className="text-xs text-slate-600 mb-1 block">
                      Minors
                    </Label>
                    <Select
                      value={String(waitlistMinors)}
                      onValueChange={(v) => setWaitlistMinors(parseInt(v, 10))}
                    >
                      <SelectTrigger id="wl-minors" className="h-9" data-testid="waitlist-minors">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 1, 2, 3].map((n) => (
                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Total requested: {waitlistTotalTickets} ticket{waitlistTotalTickets !== 1 ? "s" : ""}
                </p>
              </>
            )}
          </div>
        ) : (

        <div className="space-y-5">
          {/* Member section — single-choice: exactly 1 ticket for the member */}
          <section data-testid="custom-rows-member-section">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-emerald-700" />
              <h4 className="text-sm font-semibold text-slate-900">
                For yourself
                <span className="ml-2 text-xs font-normal text-slate-500">
                  {currentUser?.first_name_1} {currentUser?.last_name}
                </span>
              </h4>
            </div>
            <p className="text-[11px] text-slate-500 mb-2">Pick one ticket type. Members can only book one ticket for themselves per event.</p>
            <div className="space-y-1.5" role="radiogroup" aria-label="Member ticket type">
              {adultRows.map((r) => {
                const checked = memberSelectedRow === r.id;
                const soldOut = rowSoldOut(r.id);
                return (
                  <label
                    key={r.id}
                    className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md border transition-colors ${
                      soldOut ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed" : checked ? "border-emerald-400 bg-emerald-50 cursor-pointer" : "border-slate-200 hover:bg-slate-50 cursor-pointer"
                    }`}
                    data-testid={`member-row-${r.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <input
                        type="radio"
                        name="member-row"
                        checked={checked}
                        disabled={soldOut}
                        onChange={() => setMemberRow(r.id)}
                        className="accent-emerald-600 shrink-0"
                        data-testid={`member-row-radio-${r.id}`}
                      />
                      <span className="text-sm text-slate-800 truncate">{r.label}</span>
                      <CategoryLeftBadge left={leftOf(r.id)} testId={`member-row-left-${r.id}`} />
                    </div>
                    <span className="text-sm font-semibold text-slate-900 shrink-0">€{Number(r.amount).toFixed(2)}</span>
                  </label>
                );
              })}
              <button
                type="button"
                onClick={clearMemberRow}
                className={`text-[11px] mt-1 underline-offset-2 ${
                  memberSelectedRow ? "text-slate-500 hover:text-slate-700 hover:underline" : "text-slate-300 cursor-default"
                }`}
                disabled={!memberSelectedRow}
                data-testid="member-row-clear"
              >
                No ticket for me (book only for partner / minors)
              </button>
            </div>
          </section>

          {/* Partner section — single-choice: exactly 1 ticket for the partner */}
          {partner ? (
            <section className="rounded-md border border-slate-200 p-3">
              <label className="flex items-center gap-2 mb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includePartner}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setIncludePartner(on);
                    if (on) {
                      // Auto-pick the first row so total updates immediately.
                      if (!partnerSelectedRow && firstOpenAdultRowId) setPartnerRow(firstOpenAdultRowId);
                    } else {
                      clearPartnerRow();
                    }
                  }}
                  data-testid="include-partner-toggle"
                />
                <Users className="h-4 w-4 text-indigo-700" />
                <span className="text-sm font-semibold text-slate-900">
                  Also book for my partner
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    {partner.first_name} {partner.last_name} · {partner.email}
                  </span>
                </span>
              </label>
              {includePartner && (
                <>
                  <p className="text-[11px] text-slate-500 mb-2">Pick one ticket type for your partner.</p>
                  <div className="space-y-1.5" role="radiogroup" aria-label="Partner ticket type" data-testid="custom-rows-partner-section">
                    {adultRows.map((r) => {
                      const checked = partnerSelectedRow === r.id;
                      const soldOut = rowSoldOut(r.id);
                      return (
                        <label
                          key={r.id}
                          className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md border transition-colors ${
                            soldOut ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed" : checked ? "border-indigo-400 bg-indigo-50 cursor-pointer" : "border-slate-200 hover:bg-slate-50 cursor-pointer"
                          }`}
                          data-testid={`partner-row-${r.id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <input
                              type="radio"
                              name="partner-row"
                              checked={checked}
                              disabled={soldOut}
                              onChange={() => setPartnerRow(r.id)}
                              className="accent-indigo-600 shrink-0"
                              data-testid={`partner-row-radio-${r.id}`}
                            />
                            <span className="text-sm text-slate-800 truncate">{r.label}</span>
                            <CategoryLeftBadge left={leftOf(r.id)} testId={`partner-row-left-${r.id}`} />
                          </div>
                          <span className="text-sm font-semibold text-slate-900 shrink-0">€{Number(r.amount).toFixed(2)}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </section>
          ) : (
            <p className="text-xs text-slate-500 italic">Link a partner from the user menu to book tickets for them too.</p>
          )}

          {/* Minors section */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Baby className="h-4 w-4 text-amber-700" />
                <h4 className="text-sm font-semibold text-slate-900">Minors ({minors.length}/3)</h4>
              </div>
              {minors.length < 3 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addMinor}
                  className="h-7 border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="add-minor-btn"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add minor
                </Button>
              )}
            </div>
            {minors.length === 0 ? (
              <p className="text-xs text-slate-500">No minors added.</p>
            ) : (
              <div className="space-y-2">
                {minors.map((m, idx) => (
                  <div
                    key={idx}
                    className="rounded-md border border-slate-200 p-2 bg-slate-50/50 space-y-2"
                    data-testid={`minor-row-${idx}`}
                  >
                    {/* Row 1: names + remove */}
                    <div className="flex items-center gap-2">
                      <Input
                        className="flex-1 h-8"
                        placeholder="First name"
                        value={m.first_name}
                        onChange={(e) => updateMinor(idx, { first_name: e.target.value })}
                        data-testid={`minor-first-${idx}`}
                      />
                      <Input
                        className="flex-1 h-8"
                        placeholder="Last name"
                        value={m.last_name}
                        onChange={(e) => updateMinor(idx, { last_name: e.target.value })}
                        data-testid={`minor-last-${idx}`}
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 shrink-0 text-red-600 hover:bg-red-50"
                        onClick={() => removeMinor(idx)}
                        data-testid={`minor-remove-${idx}`}
                        aria-label="Remove minor"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {/* Row 2: age + ticket type (labelled, with room to breathe) */}
                    <div className="flex items-end gap-2">
                      <div className="w-20 shrink-0">
                        <Label htmlFor={`minor-age-input-${idx}`} className="text-[11px] text-slate-600 mb-1 block">
                          Age (1-17)
                        </Label>
                        <Input
                          id={`minor-age-input-${idx}`}
                          type="number"
                          min={1}
                          max={17}
                          className="h-8"
                          placeholder="Age"
                          value={m.age}
                          onChange={(e) => updateMinor(idx, { age: e.target.value })}
                          data-testid={`minor-age-${idx}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <Label htmlFor={`minor-row-select-${idx}`} className="text-[11px] text-slate-600 mb-1 block">
                          Ticket type
                        </Label>
                        <select
                          id={`minor-row-select-${idx}`}
                          className="w-full h-8 rounded-md border border-slate-200 text-sm px-2 bg-white"
                          value={m.row_id || ""}
                          onChange={(e) => updateMinor(idx, { row_id: e.target.value })}
                          data-testid={`minor-row-select-${idx}`}
                        >
                          {rows.map((r) => (
                            <option key={r.id} value={r.id} disabled={rowSoldOut(r.id)}>
                              {r.label} — €{Number(r.amount).toFixed(2)}{rowSoldOut(r.id) ? " (sold out)" : leftOf(r.id) != null && leftOf(r.id) <= LOW_STOCK_THRESHOLD ? ` (${leftOf(r.id)} left)` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Guided-visit language selector — only when the event advertises
              guided visits. Free service that admin honours on a best-effort
              basis; wording mirrors the legacy booking flow. */}
          {guidedRequired && (
            <section
              className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-3"
              data-testid="guided-visit-section"
            >
              <div className="flex items-start gap-2 mb-2">
                <Globe className="h-4 w-4 text-emerald-700 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-emerald-900 mb-2">
                    Guided visit — please pick your language
                  </p>
                  <GuidedLanguageRadios value={guidedLanguage} onChange={setGuidedLanguage} name="custom-rows-guided-language" />
                  <p className="text-[11px] text-emerald-900/80 mt-2 leading-relaxed">
                    As a free additional service, the ACE offers guided tours in English and Italian.
                    Preferences are honoured where possible, subject to availability. By booking, participants accept
                    that no reimbursement can be claimed if their preferred language is unavailable.
                  </p>
                </div>
              </div>
            </section>
          )}

          {/* Disclaimer + total */}
          <section className="rounded-lg border-2 border-red-200 bg-red-50/40 p-3" data-testid="disclaimer-banner">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-red-700 mt-0.5" />
              <p className="text-xs text-red-900 leading-relaxed">{disclaimer}</p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                data-testid="accept-disclaimer"
              />
              <span className="text-xs font-semibold text-red-900">I have read and accept the above.</span>
            </label>
          </section>

          {/* Optional free-form comments for the organiser */}
          <section className="space-y-1" data-testid="booking-comments">
            <Label htmlFor="booking-comments" className="text-xs text-slate-600">
              Add any comments <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <textarea
              id="booking-comments"
              rows={3}
              maxLength={1000}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Dietary requirements, accessibility needs, special requests…"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
              data-testid="booking-comments-input"
            />
            <p className="text-[11px] text-slate-500 text-right">{comments.length}/1000</p>
          </section>

          <section className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-1" data-testid="booking-total">
            <BreakdownLine label={`Your tickets (${Object.values(memberQty).reduce((a, b) => a + (b || 0), 0)})`} value={memberSubtotal} />
            {includePartner && (
              <BreakdownLine
                label={`Partner tickets (${Object.values(partnerQty).reduce((a, b) => a + (b || 0), 0)})`}
                value={partnerSubtotal}
              />
            )}
            {minors.length > 0 && (
              <BreakdownLine label={`Minors (${minors.length})`} value={minorsSubtotal} />
            )}
            <div className="flex items-center justify-between pt-2 border-t border-slate-200 mt-2">
              <span className="text-sm font-semibold text-slate-900">Total</span>
              <span className="text-lg font-bold text-emerald-700" data-testid="booking-total-amount">
                €{total.toFixed(2)}
              </span>
            </div>
            <p className="text-[11px] text-slate-500">
              {minors.length > 0 && (
                <>Minors keep their ticket type — the price shown is the row you picked for each minor.<br/></>
              )}
              You'll be redirected to Stripe to complete the payment.
            </p>
          </section>
        </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting || joiningWaitlist}>Cancel</Button>
          {isSoldOut ? (
            onWaitlist ? (
              <Button
                onClick={onClose}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="waitlist-close-btn"
              >
                Close
              </Button>
            ) : (
              <Button
                onClick={joinWaitlist}
                disabled={joiningWaitlist || waitlistTotalTickets < 1}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="join-waitlist-btn"
              >
                {joiningWaitlist ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Joining…</>
                ) : (
                  <><Clock className="h-4 w-4 mr-1" /> Join Waiting List</>
                )}
              </Button>
            )
          ) : (
            <Button
              onClick={submit}
              disabled={submitting || totalTickets === 0 || !accepted}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-booking-btn"
            >
              {submitting ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Redirecting…</>) : `Pay €${total.toFixed(2)}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CategoryLeftBadge = ({ left, testId }) => {
  if (left == null || left > LOW_STOCK_THRESHOLD) return null;
  return left === 0 ? (
    <Badge className="bg-red-100 text-red-700 border border-red-200 hover:bg-red-100 text-[10px] px-1.5 py-0" data-testid={testId}>Sold out</Badge>
  ) : (
    <Badge className="bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-50 text-[10px] px-1.5 py-0" data-testid={testId}>{left} left</Badge>
  );
};

const BreakdownLine = ({ label, value }) => (
  <div className="flex items-center justify-between text-xs text-slate-700">
    <span>{label}</span>
    <span>€{Number(value).toFixed(2)}</span>
  </div>
);

export default CustomRowsBookingDialog;
